import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { api, fmt } from "../lib/api";
import { t } from "../lib/i18n";
import { Search, Trophy, Sparkles, TrendingUp } from "lucide-react";

const VOL_COLOR = {
  low: "bg-emerald-900/40 text-emerald-300 border-emerald-500/30",
  medium: "bg-blue-900/40 text-blue-300 border-blue-500/30",
  high: "bg-orange-900/40 text-orange-300 border-orange-500/30",
  mega: "bg-red-900/50 text-red-300 border-red-500/40",
};

export default function SlotsCatalog() {
  const nav = useNavigate();
  const { currency, lang } = useApp();
  const [slots, setSlots] = useState([]);
  const [q, setQ] = useState("");
  const [vol, setVol] = useState("");
  const [jackpots, setJackpots] = useState({ HUF: {}, EUR: {} });
  const [bigWins, setBigWins] = useState([]);

  useEffect(() => {
    api.get("/games/slots/catalog").then(r => setSlots(r.data.slots)).catch(() => {});
    api.get("/games/slots/jackpots").then(r => setJackpots(r.data)).catch(() => {});
    api.get("/games/slots/big_wins", { params: { limit: 15 } }).then(r => setBigWins(r.data)).catch(() => {});
    const iv = setInterval(() => {
      api.get("/games/slots/jackpots").then(r => setJackpots(r.data)).catch(() => {});
    }, 15000);
    return () => clearInterval(iv);
  }, []);

  const tiers = jackpots[currency] || {};
  const filtered = slots.filter(s => {
    if (q && !s.name.toLowerCase().includes(q.toLowerCase()) && !s.theme.includes(q.toLowerCase())) return false;
    if (vol && s.vol !== vol) return false;
    return true;
  });

  return (
    <div className="max-w-[1500px] mx-auto px-6 py-10">
      <div className="flex items-center gap-3 mb-2">
        <Sparkles className="text-[#d4af37]" size={32} />
        <h1 className="font-serif text-4xl">Slots</h1>
        <span className="text-xs text-white/40 uppercase tracking-widest ml-2">50 machines · Progressive Jackpots</span>
      </div>

      {/* 4-tier progressive jackpot ticker */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { k: "mega", label: "MEGA", color: "from-red-600 to-yellow-500" },
          { k: "major", label: "MAJOR", color: "from-purple-600 to-pink-500" },
          { k: "minor", label: "MINOR", color: "from-blue-600 to-cyan-400" },
          { k: "mini", label: "MINI", color: "from-emerald-600 to-teal-400" },
        ].map(t => (
          <div key={t.k} data-testid={`jackpot-${t.k}`}
               className={`relative overflow-hidden lux-card p-4 border-2 border-transparent bg-gradient-to-br ${t.color}`}>
            <div className="absolute inset-0 bg-black/70"/>
            <div className="relative">
              <div className={`text-[10px] uppercase tracking-[0.4em] bg-gradient-to-r ${t.color} bg-clip-text text-transparent font-bold`}>
                {t.label} · JACKPOT
              </div>
              <div className="font-serif text-2xl md:text-3xl mt-1 gold-text truncate">
                {fmt(tiers[t.k] || 0, currency)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Big wins ticker */}
      {bigWins.length > 0 && (
        <div className="lux-card p-3 mb-6 flex items-center gap-3 overflow-x-auto">
          <div className="flex items-center gap-1 shrink-0 text-xs uppercase tracking-widest text-[#d4af37]">
            <Trophy size={14}/>Recent Wins
          </div>
          {bigWins.map((w, i) => (
            <div key={i} className="shrink-0 text-xs bg-black/50 rounded px-3 py-1 border border-[#d4af37]/20">
              <span className="text-[#d4af37] font-bold">{w.user_name}</span>{" "}
              won <span className="text-emerald-400 font-bold">{fmt(w.amount, w.currency)}</span>{" "}
              <span className="text-white/40">on {w.slot_name} · {w.tier.toUpperCase()}</span>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16}/>
          <input data-testid="slots-search" value={q} onChange={e=>setQ(e.target.value)}
                 placeholder="Search slot..."
                 className="w-full bg-[#1a1a1a] border border-white/10 rounded pl-10 pr-3 py-2 text-sm focus:border-[#d4af37] focus:outline-none"/>
        </div>
        <div className="flex gap-1">
          {["", "low", "medium", "high", "mega"].map(v => (
            <button key={v || "all"} data-testid={`vol-${v || "all"}`}
                    onClick={() => setVol(v)}
                    className={`px-3 py-2 rounded text-[10px] uppercase tracking-widest ${vol === v ? "btn-gold" : "btn-ghost"}`}>
              {v || "All"}
            </button>
          ))}
        </div>
      </div>

      {/* Slot cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {filtered.map(s => (
          <button key={s.id} data-testid={`slot-card-${s.id}`}
                  onClick={()=>nav(`/slots/play/${s.id}`)}
                  className="lux-card overflow-hidden group text-left">
            <div className="aspect-[3/4] relative flex flex-col items-center justify-center gap-1 p-2"
                 style={{background: s.bg}}>
              <div className="absolute inset-0 opacity-20"
                   style={{ backgroundImage: "radial-gradient(circle at 30% 20%, rgba(255,215,0,.3), transparent 50%)" }}/>
              <div className="relative grid grid-cols-3 gap-1 text-3xl md:text-4xl mb-2">
                {s.symbols.slice(0, 3).map((sy, i) => <div key={i}>{sy}</div>)}
              </div>
              <div className="relative text-[10px] font-serif italic text-white/60">{s.theme}</div>
              <div className={`relative mt-1 border rounded px-2 py-0.5 text-[8px] uppercase tracking-widest ${VOL_COLOR[s.vol]}`}>
                {s.vol} vol
              </div>
            </div>
            <div className="p-3 border-t border-white/5">
              <h3 className="font-serif text-sm truncate group-hover:text-[#d4af37] transition">{s.name}</h3>
              <div className="text-[10px] text-white/40 mt-0.5">Tap to play →</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
